import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, ADMIN_EMAIL } from "./firebase";
import {
  onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut,
} from "firebase/auth";
import {
  ensureUserDoc, getMe, syncGuest, setClientAdmin,
  addFavorite as fbAddFavorite, removeFavorite as fbRemoveFavorite,
  upsertCart as fbUpsertCart, removeCart as fbRemoveCart,
} from "./api";

const AuthCtx = createContext(null);

const LS_FAVS = "fd_guest_favorites";
const LS_CART = "fd_guest_cart";

function readGuestFavorites() {
  try { return JSON.parse(localStorage.getItem(LS_FAVS) || "[]"); } catch { return []; }
}
function writeGuestFavorites(arr) { localStorage.setItem(LS_FAVS, JSON.stringify(arr || [])); }
function readGuestCart() {
  try { return JSON.parse(localStorage.getItem(LS_CART) || "[]"); } catch { return []; }
}
function writeGuestCart(arr) { localStorage.setItem(LS_CART, JSON.stringify(arr || [])); }

const AUTH_ERRORS = {
  "auth/invalid-credential": "Yanlış email və ya parol",
  "auth/invalid-login-credentials": "Yanlış email və ya parol",
  "auth/wrong-password": "Yanlış parol",
  "auth/user-not-found": "İstifadəçi tapılmadı",
  "auth/invalid-email": "Yanlış email formatı",
  "auth/email-already-in-use": "Bu email artıq qeydiyyatdan keçib",
  "auth/weak-password": "Parol çox zəifdir (minimum 6 simvol)",
  "auth/too-many-requests": "Çox cəhd edildi. Bir az sonra yenidən yoxlayın",
  "auth/network-request-failed": "Şəbəkə xətası. İnternet bağlantınızı yoxlayın",
};
function authError(err) {
  const msg = AUTH_ERRORS[err && err.code] || (err && err.message) || "Giriş xətası";
  const e = new Error(msg);
  e.response = { status: 400, data: { detail: msg } };
  return e;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFromServer = useCallback(async () => {
    const me = await getMe();
    setUser(me);
    setFavorites(me.favorites || []);
    setCart(me.cart || []);
    setClientAdmin(me.role === "admin");
    return me;
  }, []);

  const syncGuestIfAny = useCallback(async () => {
    const gFavs = readGuestFavorites();
    const gCart = readGuestCart();
    if (gFavs.length === 0 && gCart.length === 0) return;
    try {
      await syncGuest({ favorites: gFavs, cart: gCart });
      writeGuestFavorites([]);
      writeGuestCart([]);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          await ensureUserDoc(fbUser);
          await syncGuestIfAny();
          await loadFromServer();
        } catch (e) { /* eslint-disable-next-line no-console */ console.error("auth load error", e); }
        finally { setLoading(false); }
      } else {
        setUser(null);
        setClientAdmin(false);
        setFavorites(readGuestFavorites());
        setCart(readGuestCart());
        setLoading(false);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, String(email).trim(), password);
      const fbUser = auth.currentUser;
      await ensureUserDoc(fbUser);
      await syncGuestIfAny();
      return await loadFromServer();
    } catch (err) { throw authError(err); }
  };

  const register = async (payload) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, String(payload.email).trim(), payload.password);
      await ensureUserDoc(cred.user, { name: (payload.name || "").trim(), surname: (payload.surname || "").trim() });
      await syncGuestIfAny();
      return await loadFromServer();
    } catch (err) { throw authError(err); }
  };

  const logout = async () => {
    try { await signOut(auth); } catch { /* ignore */ }
    setUser(null);
    setClientAdmin(false);
    setFavorites(readGuestFavorites());
    setCart(readGuestCart());
  };

  // --------- Favorites ---------
  const isFavorite = (doorId) => favorites.includes(doorId);
  const toggleFavorite = async (doorId) => {
    const wasFav = favorites.includes(doorId);
    const next = wasFav ? favorites.filter((x) => x !== doorId) : [...favorites, doorId];
    setFavorites(next);
    if (user) {
      try { if (wasFav) await fbRemoveFavorite(doorId); else await fbAddFavorite(doorId); }
      catch { setFavorites(favorites); }
    } else { writeGuestFavorites(next); }
    return !wasFav;
  };

  // --------- Cart ---------
  const cartCount = cart.reduce((s, c) => s + (c.qty || 1), 0);
  const inCart = (doorId) => cart.some((c) => c.door_id === doorId);
  const getQty = (doorId) => (cart.find((c) => c.door_id === doorId) || {}).qty || 0;

  const setCartQty = async (doorId, qty) => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    const exists = cart.find((c) => c.door_id === doorId);
    const next = exists
      ? cart.map((c) => (c.door_id === doorId ? { ...c, qty: q } : c))
      : [...cart, { door_id: doorId, qty: q }];
    setCart(next);
    if (user) { try { await fbUpsertCart(doorId, q); } catch { setCart(cart); } }
    else { writeGuestCart(next); }
  };
  const addToCart = (doorId) => setCartQty(doorId, (getQty(doorId) || 0) + 1);

  const removeFromCart = async (doorId) => {
    const next = cart.filter((c) => c.door_id !== doorId);
    setCart(next);
    if (user) { try { await fbRemoveCart(doorId); } catch { setCart(cart); } }
    else { writeGuestCart(next); }
  };

  const clearCart = async () => {
    const current = [...cart];
    setCart([]);
    if (user) { await Promise.all(current.map((c) => fbRemoveCart(c.door_id).catch(() => {}))); }
    else { writeGuestCart([]); }
  };

  const isAdmin = !!user && (user.role === "admin" || (user.email || "").toLowerCase() === ADMIN_EMAIL);

  return (
    <AuthCtx.Provider value={{
      user, loading, isAdmin,
      login, register, logout,
      favorites, isFavorite, toggleFavorite,
      cart, cartCount, inCart, getQty, addToCart, setCartQty, removeFromCart, clearCart,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
